import type { ProjectMapResponse } from '@/services/realtimeClient';
import {
  DEFAULT_PROJECT_FILTERS,
  projectFiltersFromParams,
  projectPortfolioFilters,
  projectViewFromParams,
} from './projectFilters';
import type { ProjectPage, ProjectPortfolioSummary } from './projects.types';

/** A map is a view of business membership, never a view of the current register page. */
export function portfolioMapParams(params: URLSearchParams) {
  const filters = projectFiltersFromParams(params);
  const next = new URLSearchParams();
  for (const [key, value] of Object.entries(projectPortfolioFilters(filters)))
    if (value) next.set(key, value);
  if (filters.page > 1) next.set('page', String(filters.page));
  if (filters.sortBy !== DEFAULT_PROJECT_FILTERS.sortBy) next.set('sortBy', filters.sortBy);
  if (filters.sortOrder !== DEFAULT_PROJECT_FILTERS.sortOrder)
    next.set('sortOrder', filters.sortOrder);
  const view = projectViewFromParams(params);
  if (view !== 'dashboard') next.set('view', view);
  return next;
}

export function portfolioMapHref(params: URLSearchParams) {
  const next = portfolioMapParams(params);
  next.set('portfolio', '1');
  return `/projects/map?${next}`;
}

export function portfolioMapReturnHref(params: URLSearchParams) {
  const query = portfolioMapParams(params).toString();
  return `/projects${query ? `?${query}` : ''}`;
}

export interface PortfolioMapScope {
  matchingCount: number;
  includedCount: number;
  excludedCount: number;
  unmappedCount: number;
  projectIds: string[];
  recordedHealth: Record<string, number | null>;
}
export interface PortfolioMapSnapshot {
  map: ProjectMapResponse;
  scope: PortfolioMapScope;
}

export class PortfolioMapSourceError extends Error {
  readonly status = 409;
  constructor() {
    super(
      'The complete Portfolio cohort or map source changed while loading. Refresh this same scope to retry.',
    );
    this.name = 'PortfolioMapSourceError';
  }
}

const fail = () => {
  throw new PortfolioMapSourceError();
};
function summaryFacts(summary: ProjectPortfolioSummary) {
  const { generatedAt: _generatedAt, ...facts } = summary;
  return JSON.stringify(facts);
}
function coordinate(value: string | number | null, limit: number): number | null {
  if (value === null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) && Math.abs(number) <= limit ? number : null;
}
function cents(value: string): bigint {
  if (!/^-?\d+(?:\.\d{1,2})?$/.test(value)) return fail();
  const negative = value.startsWith('-'),
    [whole, fraction = ''] = value.replace(/^-/, '').split('.');
  return BigInt(`${whole}${fraction.padEnd(2, '0')}`) * (negative ? -1n : 1n);
}
function amount(value: bigint): string {
  const absolute = value < 0n ? -value : value;
  return `${value < 0n ? '-' : ''}${absolute / 100n}.${String(absolute % 100n).padStart(2, '0')}`;
}

/** Intersect only after terminal pagination and both source reads reconcile. */
export function scopePortfolioMap(
  cohort: ProjectPage,
  snapshot: ProjectMapResponse,
  before: ProjectPortfolioSummary,
  after: ProjectPortfolioSummary,
): PortfolioMapSnapshot {
  if (
    !Array.isArray(cohort.items) ||
    !Array.isArray(snapshot.markers) ||
    cohort.items.length !== cohort.meta.total ||
    new Set(cohort.items.map((project) => project.id)).size !== cohort.items.length ||
    snapshot.markers.length !== snapshot.metrics.projects ||
    new Set(snapshot.markers.map((marker) => marker.id)).size !== snapshot.markers.length ||
    summaryFacts(before) !== summaryFacts(after) ||
    after.matchingRegisterCount !== cohort.items.length
  )
    return fail();
  const included = cohort.items.filter((project) => !project.portfolio.excluded);
  if (
    included.length !== after.portfolioCount ||
    cohort.items.length - included.length !== after.excludedCount
  )
    return fail();
  const latestTime = cohort.items.reduce(
    (latest, project) => Math.max(latest, Date.parse(project.updatedAt)),
    0,
  );
  if (!Number.isFinite(latestTime)) return fail();
  const latest = cohort.items.length ? new Date(latestTime).toISOString() : null;
  if (latest !== after.latestProjectUpdatedAt) return fail();
  const byId = new Map(included.map((project) => [project.id, project]));
  const markers = snapshot.markers.filter((marker) => byId.has(marker.id));
  const located = included.filter(
    (project) =>
      coordinate(project.location.latitude, 90) !== null &&
      coordinate(project.location.longitude, 180) !== null,
  );
  if (located.length !== markers.length) return fail();
  for (const marker of markers) {
    const project = byId.get(marker.id)!;
    if (
      project.projectNumber !== marker.projectNumber ||
      project.name !== marker.name ||
      project.status !== marker.status ||
      project.phase !== marker.phase ||
      project.riskLevel !== marker.riskLevel ||
      project.sector !== marker.sector ||
      coordinate(project.location.latitude, 90) !== marker.location.latitude ||
      coordinate(project.location.longitude, 180) !== marker.location.longitude ||
      (project.financials === null) !== (marker.financials === null)
    )
      return fail();
    if (project.financials && marker.financials)
      for (const key of [
        'contractValue',
        'budgetCurrent',
        'actualCost',
        'forecastAtCompletion',
      ] as const)
        if (cents(project.financials[key]) !== cents(marker.financials[key])) return fail();
    if (
      marker.activeAlerts === null ||
      !Number.isSafeInteger(marker.activeAlerts) ||
      marker.activeAlerts < 0
    )
      return fail();
  }
  const latitude = markers.map((marker) => marker.location.latitude);
  const longitude = markers.map((marker) => marker.location.longitude);
  const finance =
    snapshot.metrics.portfolioValue !== null &&
    markers.every((marker) => marker.financials !== null);
  return {
    scope: {
      matchingCount: cohort.items.length,
      includedCount: included.length,
      excludedCount: after.excludedCount,
      unmappedCount: included.length - markers.length,
      projectIds: included.map((project) => project.id),
      recordedHealth: Object.fromEntries(
        included.map((project) => [
          project.id,
          project.health.source?.availability === 'recorded_model'
            ? project.health.source.score
            : null,
        ]),
      ),
    },
    map: {
      ...snapshot,
      markers,
      map: {
        center: markers.length
          ? {
              latitude: latitude.reduce((sum, value) => sum + value, 0) / markers.length,
              longitude: longitude.reduce((sum, value) => sum + value, 0) / markers.length,
            }
          : null,
        bounds: markers.length
          ? {
              north: Math.max(...latitude),
              south: Math.min(...latitude),
              east: Math.max(...longitude),
              west: Math.min(...longitude),
            }
          : null,
      },
      metrics: {
        projects: markers.length,
        criticalProjects: markers.filter((marker) => marker.riskLevel === 'CRITICAL').length,
        openAlerts: markers.reduce((sum, marker) => sum + marker.activeAlerts!, 0),
        portfolioValue: finance
          ? amount(
              markers.reduce((sum, marker) => sum + cents(marker.financials!.contractValue), 0n),
            )
          : null,
        forecastVariance: finance
          ? amount(
              markers.reduce(
                (sum, marker) =>
                  sum +
                  cents(marker.financials!.forecastAtCompletion) -
                  cents(marker.financials!.budgetCurrent),
                0n,
              ),
            )
          : null,
      },
    },
  };
}
